import type { Camera } from '@/types';
import { camerasEqual } from './cameraOps';

/** Ease-out cubic: fast start, gentle settle — reads as "arriving" rather than "sliding". */
export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Interpolates cameras. Zoom is interpolated in log space so a 4× zoom feels
 * uniform across its duration instead of rushing the first half.
 */
export const interpolateCamera = (from: Camera, to: Camera, t: number): Camera => {
  const k = Math.min(1, Math.max(0, t));
  const zoom = Math.exp(Math.log(from.zoom) + (Math.log(to.zoom) - Math.log(from.zoom)) * k);
  return {
    zoom,
    position: {
      x: from.position.x + (to.position.x - from.position.x) * k,
      y: from.position.y + (to.position.y - from.position.y) * k,
    },
  };
};

/**
 * Duration scaled by how far the camera travels (screen distance at the target zoom
 * plus zoom ratio), clamped so short hops are snappy and long ones stay bounded.
 */
export const transitionDuration = (from: Camera, to: Camera, viewportWidth: number): number => {
  if (camerasEqual(from, to)) return 0;
  const dx = (to.position.x - from.position.x) * to.zoom;
  const dy = (to.position.y - from.position.y) * to.zoom;
  const travel = Math.hypot(dx, dy) / Math.max(1, viewportWidth);
  const zoomRatio = Math.abs(Math.log(to.zoom / from.zoom));
  return Math.round(Math.min(700, Math.max(180, 220 + travel * 160 + zoomRatio * 120)));
};

export interface CameraTransition {
  readonly from: Camera;
  readonly to: Camera;
  readonly startedAt: number;
  readonly duration: number;
}

/** Camera at `now` for a transition; `done` once the end has been reached. */
export const sampleTransition = (t: CameraTransition, now: number): { camera: Camera; done: boolean } => {
  if (t.duration <= 0) return { camera: t.to, done: true };
  const k = (now - t.startedAt) / t.duration;
  if (k >= 1) return { camera: t.to, done: true };
  return { camera: interpolateCamera(t.from, t.to, easeOutCubic(k)), done: false };
};
